import { multiSelectOptionValue } from './multiSelectAnswer';

const SUPPORTED_TRIGGER_FIELDS = ['select', 'multi-select', 'multiple-choice', 'checkbox'];

type LogicOption = { _id?: unknown; label?: string | null; value?: string | null };

export type LogicField = {
  _id?: unknown;
  element?: string | null;
  options?: LogicOption[] | null;
  value?: unknown;
  showHideLogic?: {
    enabled?: boolean | null;
    comparison?: string | null;
    value?: unknown;
    element?: { elementId?: unknown } | null;
  } | null;
};

export type LogicFormFields = Record<string, LogicField | undefined>;

// Like lodash's isEmpty: numbers and booleans count as empty.
function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return true;
}

function parseBoolean(value: unknown): boolean {
  if (value === true || value === false) return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
}

function compareFieldValues(
  comparison: string | null | undefined,
  conditionalValue: unknown,
  logicValue: unknown,
  fieldType: string | null | undefined,
): boolean {
  if (fieldType === 'checkbox') {
    switch (comparison) {
      case 'IS':
        return parseBoolean(conditionalValue) === parseBoolean(logicValue);
      case 'IS_NOT':
        return parseBoolean(conditionalValue) !== parseBoolean(logicValue);
      default:
        return false;
    }
  }

  switch (comparison) {
    case 'IS':
      return Array.isArray(conditionalValue)
        ? conditionalValue.includes(logicValue)
        : conditionalValue === logicValue;
    case 'IS_NOT':
      return Array.isArray(conditionalValue)
        ? !conditionalValue.includes(logicValue)
        : conditionalValue !== logicValue;
    case 'IS_EMPTY':
      return isEmpty(conditionalValue);
    case 'IS_NOT_EMPTY':
      return !isEmpty(conditionalValue);
    default:
      return false;
  }
}

function getConditionalValue(fieldId: unknown, formFields: LogicFormFields): unknown {
  const field = formFields[String(fieldId)];
  if (!field) return null;

  switch (field.element) {
    case 'checkbox':
      return parseBoolean(field.value);
    case 'multiple-choice':
      return field.options?.find(o => o.label === field.value)?._id;
    case 'multi-select': {
      // A list of option ids; IS and IS_NOT then mean "includes" and "does not include".
      const chosen = Array.isArray(field.value) ? field.value : [];
      return (field.options ?? [])
        .filter(o => chosen.includes(multiSelectOptionValue(o as any)))
        .map(o => o._id);
    }
    case 'select': {
      const option =
        field.options?.find(o => o.value === field.value) ??
        field.options?.find(o => o.label === field.value);
      return option?._id;
    }
    default:
      return field.value;
  }
}

/**
 * Whether a field shows, given its conditional logic and the current answers. A field that
 * depends on a hidden field is hidden too; a circular dependency hides it.
 */
export function isFieldEnabled(
  field: LogicField | null | undefined,
  formFields: LogicFormFields = {},
  pageFields: LogicField[] = [],
  visited: Set<unknown> = new Set(),
): boolean {
  if (!field?.showHideLogic?.enabled) return true;

  const { element, comparison, value: logicValue } = field.showHideLogic;
  const comparisonField = pageFields.find(f => f._id === element?.elementId);

  if (!comparisonField || !SUPPORTED_TRIGGER_FIELDS.includes(comparisonField.element ?? '')) {
    return true;
  }

  const fieldId = field._id;
  if (visited.has(fieldId)) return false;
  visited.add(fieldId);

  if (!isFieldEnabled(comparisonField, formFields, pageFields, visited)) {
    visited.delete(fieldId);
    return false;
  }

  const shouldShow = compareFieldValues(
    comparison,
    getConditionalValue(comparisonField._id, formFields),
    logicValue,
    comparisonField.element,
  );

  visited.delete(fieldId);
  return shouldShow;
}
